/**
 * ชิ้นส่วนที่เอกสาร PDF ทุกใบในระบบใช้ร่วมกัน
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องรวมไว้ที่เดียว
 * ────────────────────────────────────────────────────────────────
 *
 * เดิม profilePdf.ts มีตัวช่วยจัดรูปแบบชุดหนึ่งเป็นของตัวเอง (thaiDate · monthsTH ·
 * levelLabel · layout ของตาราง · ชุด styles) พอมี positionPdf.ts เพิ่มเข้ามาอีกใบ
 * ทางที่ง่ายที่สุดคือลอกทั้งชุดไปอีกก้อน ซึ่งจะทำให้เอกสารสองใบที่ออกจากระบบเดียวกัน
 * เริ่มเพี้ยนจากกันภายในเดือนเดียว — วันเดือนปีคนละรูปแบบ หัวข้อคนละขนาด
 * เส้นตารางคนละสี โดยไม่มีใครสังเกตจนกว่าจะเอาสองไฟล์มาวางข้างกัน
 *
 * ⚠ เอกสารใหม่ต้องดึงจากที่นี่ ห้ามลอกฟังก์ชันเหล่านี้ไปไว้ในไฟล์ของตัวเอง
 *   ถ้าต้องการรูปแบบที่ต่างออกไปจริง ๆ ให้เพิ่มพารามิเตอร์ที่นี่ ไม่ใช่แยกสำเนา
 *
 * ────────────────────────────────────────────────────────────────
 *  สิ่งที่ไม่ได้อยู่ในนี้
 * ────────────────────────────────────────────────────────────────
 *
 * โครงเอกสาร (จะมีหัวข้ออะไร เรียงยังไง) เป็นของแต่ละไฟล์ ไม่ใช่ของที่นี่
 * เอกสารประวัติรายบุคคลกับเอกสารรายละเอียดอัตราตอบคนละคำถาม การบังคับให้ใช้
 * โครงเดียวกันจะทำให้ทั้งสองใบไม่ตอบคำถามไหนได้ดีเลย
 */

/** ระยะขอบของกระดาษ A4 แนวตั้ง — ซ้าย บน ขวา ล่าง */
export const PAGE_MARGINS: [number, number, number, number] = [40, 44, 40, 52];

/**
 * โหลด pdfmake พร้อมฟอนต์ Sarabun — เรียกตอนกดปุ่มเท่านั้น
 *
 * ⚠ ห้าม import ทั้งสองอย่างไว้บนหัวไฟล์ รวมกันหนักกว่า 400 KB
 *   คนส่วนใหญ่เปิดระบบมาดูผังแล้วออก ไม่เคยกดบันทึกไฟล์เลย
 *
 * ⚠ ฟอนต์ต้องเป็นตัวเดียวกับที่หน้าจอใช้ (styles.css ฝัง Sarabun ชุดเดียวกัน)
 *   ไม่งั้นสิ่งที่เห็นบนจอกับสิ่งที่พิมพ์ออกมาจะมีเมตริกต่างกัน ตารางที่จัดพอดีจะล้น
 */
export async function loadPdfMake() {
  const [{ default: pdfMake }, fonts] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('./sarabun.js'),
  ]);

  pdfMake.addVirtualFileSystem({
    'Sarabun-Regular.ttf': fonts.SARABUN_REGULAR,
    'Sarabun-SemiBold.ttf': fonts.SARABUN_SEMIBOLD,
  });
  pdfMake.addFonts({
    Sarabun: {
      normal: 'Sarabun-Regular.ttf',
      bold: 'Sarabun-SemiBold.ttf',
      italics: 'Sarabun-Regular.ttf',
      bolditalics: 'Sarabun-SemiBold.ttf',
    },
  });

  return pdfMake;
}

export type Cell = {
  text: string;
  style?: string;
  noWrap?: boolean;
  colSpan?: number;
  margin: [number, number, number, number];
};
export type Row = Cell[];

/**
 * ช่องหนึ่งช่องในตาราง
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมเว้นระยะด้วย margin ของเนื้อหา ไม่ใช่ padding ของ layout
 * ────────────────────────────────────────────────────────────────
 *
 * pdfmake คำนวณความกว้างของคอลัมน์แบบ '*' จาก "ความกว้างที่เหลือหลังหักคอลัมน์ที่กำหนดตายตัว"
 * โดย **ไม่ได้หัก padding ของ layout ออกก่อน** ตารางจึงกว้างเกินขอบกระดาษเท่ากับผลรวม padding พอดี
 *
 * เคยเจอมาแล้วจริง ๆ กับตารางประวัติ 5 คอลัมน์ที่ padding ข้างละ 8 จุด รวม 40 จุด
 * ผลคือคอลัมน์ "เลขที่คำสั่ง" ถูกตัดหายไปนอกหน้ากระดาษ โดยที่บนหน้าจอไม่มีอะไรผิด
 *
 * ตั้ง padding เป็นศูนย์แล้วเว้นระยะด้วย margin ของเนื้อหาแทน — margin อยู่ข้างในช่อง
 * จึงไม่ไปเพิ่มความกว้างของตาราง ตารางกว้างเท่าที่สั่งไว้เสมอ
 */
export function td(text: string, style?: string, noWrap = false): Cell {
  return { text, style, noWrap, margin: [0, 5, 10, 5] };
}

/** กรอบรอบช่องสรุปตัวเลข — มีเส้นทุกด้าน */
export const boxLayout = {
  hLineWidth: () => 0.7,
  vLineWidth: () => 0.7,
  hLineColor: () => '#e2d8bf',
  vLineColor: () => '#e2d8bf',
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/** ตารางรายการ — มีแต่เส้นคั่นแถว ระยะห่างมาจาก margin ของเนื้อหาในช่อง (ดู td) */
export const rowLayout = {
  hLineWidth: (i: number) => (i === 0 ? 0 : 0.6),
  vLineWidth: () => 0,
  hLineColor: () => '#efe8d8',
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/**
 * ชุดสไตล์ของเอกสาร — ล้อสีของธีมบนหน้าจอ
 *
 * ⚠ เขียนเป็นค่าสีตรง ๆ ไม่ใช่อ่านจาก CSS variable โดยตั้งใจ
 *   pdfmake ทำงานนอก DOM และเอกสารต้องออกมาเหมือนกันทุกครั้ง
 *   ถ้าอ่านจากธีม คนที่เปิดโหมดมืดอยู่จะได้ไฟล์ตัวหนังสือสีจางบนกระดาษขาว
 */
export const DOC_STYLES = {
  h1: { fontSize: 19, bold: true, margin: [0, 0, 0, 2] },
  lead: { fontSize: 12, color: '#3d4a61' },
  unit: { fontSize: 9.5, color: '#67748c', margin: [0, 2, 0, 0] },
  h2: { fontSize: 12.5, bold: true, margin: [0, 18, 0, 6], color: '#16336b' },
  small: { fontSize: 9, color: '#67748c' },
  th: { fontSize: 9, bold: true, color: '#3d4a61' },
  cellLabel: { fontSize: 8.5, color: '#67748c' },
  cellValue: { fontSize: 12, bold: true },
  cellNote: { fontSize: 8, color: '#94a0b5' },
  warn: { fontSize: 9.5, color: '#85691c' },
  alert: { fontSize: 9.5, color: '#a83a29', bold: true },
  foot: { fontSize: 8, color: '#94a0b5' },
  none: { fontSize: 9.5, color: '#94a0b5', italics: true },
};

/** ช่องสรุปหนึ่งช่อง — ป้าย ค่า และหมายเหตุใต้ค่า */
export function factBox(label: string, value: string, note = '') {
  return {
    stack: [
      { text: label, style: 'cellLabel' },
      { text: value, style: 'cellValue' },
      ...(note ? [{ text: note, style: 'cellNote' }] : []),
    ],
    margin: [8, 7, 8, 8] as [number, number, number, number],
  };
}

/**
 * ท้ายกระดาษมาตรฐาน — เลขหน้าต้องอยู่ทุกหน้า
 *
 * เอกสารแบบนี้ถูกถ่ายเอกสารแจกในห้องประชุมแล้วกระดาษสลับกันได้ง่าย
 */
export function docFooter() {
  return (page: number, total: number) => ({
    columns: [
      { text: 'พิมพ์จาก YANA ระบบวิเคราะห์และวางแผนอัตรากำลัง', style: 'foot' },
      { text: `หน้า ${page} / ${total}`, style: 'foot', alignment: 'right' },
    ],
    margin: [40, 12, 40, 0] as [number, number, number, number],
  });
}

/**
 * ชื่อไฟล์ที่ระบบไฟล์ของทุกแพลตฟอร์มรับได้
 *
 * วินโดวส์ไม่ยอมรับ \ / : * ? " < > | และไม่ยอมให้ลงท้ายด้วยจุดหรือช่องว่าง
 */
export function pdfFileName(parts: string[]): string {
  const safe = parts
    .filter(Boolean)
    .join('-')
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '')
    .slice(0, 90);
  return `${safe || 'เอกสาร'}.pdf`;
}

/** วันเวลาที่ส่งออก — ต้องอยู่ในไฟล์ เพราะข้อมูลกำลังคนเปลี่ยนทุกวัน */
export function stamp(): string {
  const th = new Intl.DateTimeFormat('th-TH', { dateStyle: 'long', timeStyle: 'short' }).format(
    new Date(),
  );
  return `ส่งออกเมื่อ ${th}`;
}

/* ------------------------------------------------------------------ */
/* ตัวช่วยจัดรูปแบบ — ตั้งใจซ้ำกับหน้าจอ เพื่อให้ไฟล์กับจอตรงกันเป๊ะ     */
/* ------------------------------------------------------------------ */

export function levelLabel(level: string): string {
  const v = level.trim();
  if (!v) return '—';
  if (v.startsWith('ลูกจ้าง') || v.startsWith('กลุ่มงาน') || v.startsWith('ระดับ')) return v;
  return `ระดับ${v}`;
}

/**
 * ระดับแบบไม่มีคำว่า "ระดับ" นำหน้า สำหรับใส่ในตารางที่หัวคอลัมน์เขียนว่า "ระดับ" อยู่แล้ว
 *
 * เขียนซ้ำทั้งหัวคอลัมน์และในช่อง ทำให้ค่าที่ยาวที่สุด ("ระดับชำนาญการพิเศษ")
 * กว้างเกินคอลัมน์จนขึ้นบรรทัดใหม่กลางคำเป็น "พิ / เศษ" ทุกแถวที่เป็นระดับนั้น
 * ตัดคำนำหน้าออกแล้วทั้งตารางพอดีบรรทัดเดียว และไม่เสียความหมายเพราะหัวคอลัมน์บอกอยู่แล้ว
 */
export function bareLevel(raw: string | null | undefined): string {
  if (!raw || !raw.trim()) return 'ระบุไม่ได้';
  return raw.replace(/^ระดับ\s*/, '');
}

export function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function monthsTH(months: number): string {
  if (months <= 0) return 'ไม่ถึง 1 เดือน';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y > 0 ? `${y} ปี` : '', m > 0 ? `${m} เดือน` : ''].filter(Boolean).join(' ');
}

export function sinceText(iso: string | null): string {
  const months = monthsBetween(iso, new Date());
  return months === null ? '—' : monthsTH(months);
}

export function untilText(iso: string | null): string {
  const months = monthsBetween(new Date(), iso);
  if (months === null) return '—';
  return months < 0 ? 'ครบกำหนดแล้ว' : `อีก ${monthsTH(months)}`;
}

export function monthsBetween(
  from: string | Date | null,
  to: string | Date | null,
): number | null {
  if (!from || !to) return null;
  const a = from instanceof Date ? from : new Date(from);
  const b = to instanceof Date ? to : new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) months -= 1;
  return months;
}
